import { type MRT_PaginationState, type MRT_Updater, ShadcnReactTable } from '@talentmesh/table';
import { useMemo } from 'react';
import { useTeamMembersContext } from '../Context/TeamMembersContext';
import NoTeamMembersOverlay from './Components/NoTeamMembersOverlay';
import TeamMemberInvitationRevokeDialog from './Components/TeamMemberInvitationRevokeDialog';
import TeamMemberRemoveDialog from './Components/TeamMemberRemoveDialog';
import createTeamMembersTableColumns from './Hooks/useTeamMembersTableColumns';
import useShadcnTable from '@/Hooks/useShadcnTable';
import { TeamMember } from '@/Models/TeamMember';
import PaginationConsts from '@/Utils/PaginationConsts';

export interface TeamMembersTableDialogs {
    removeMember?: TeamMember;
    revokeInvitee?: TeamMember;
}

const columns = createTeamMembersTableColumns();

const TeamMembersTable = () => {
    const { teamMembers, isOperationLoading, totalMembersCount, pageNumber, handlePaginationModelChange } =
        useTeamMembersContext();

    const pagination = useMemo(
        () => ({
            pageIndex: pageNumber,
            pageSize: PaginationConsts.regularPageSize,
        }),
        [pageNumber],
    );

    const table = useShadcnTable({
        columns,
        data: teamMembers,
        autoResetPageIndex: false,
        getRowId: (row) => row.id,
        enableRowSelection: false,
        enableColumnDragging: false,
        enableColumnOrdering: false,
        enableColumnPinning: false,
        enableColumnResizing: false,
        enableStickyHeader: false,
        enableTableFooter: false,
        manualFiltering: false,
        manualSorting: false,
        manualPagination: true,
        rowCount: totalMembersCount,
        enableBottomToolbar: true,
        enablePagination: true,
        mantinePaginationProps: { showRowsPerPage: false },
        state: {
            isLoading: isOperationLoading,
            pagination,
        },
        onPaginationChange: (updater: MRT_Updater<MRT_PaginationState>) => {
            const next = typeof updater === 'function' ? updater(pagination) : updater;
            handlePaginationModelChange({ page: next.pageIndex, pageSize: next.pageSize });
        },
        renderEmptyRowsFallback: () => <NoTeamMembersOverlay />,
        mantinePaperProps: { className: 'rounded-xl shadow-none' },
        mantineBottomToolbarProps: { className: 'shadow-none' },
    });

    return (
        <>
            <ShadcnReactTable table={table} />
            <TeamMemberInvitationRevokeDialog />
            <TeamMemberRemoveDialog />
        </>
    );
};

export default TeamMembersTable;
